import { AlertType } from '@/public/components/Alert/types'
import { Controller } from '@hotwired/stimulus'

export type BidPlacedDetail = {
    lotNumber: number
    price: number
}

export type BidOutbidDetail = {
    lotNumber: number
    price: number
    leaderUserHash?: string
}

type BidAddResponse = {
    error?: string
    success?: boolean
    errorCode?: string
    price?: number
    leaderUserHash?: string
}

const DEFAULT_ERROR_MESSAGE = 'Произошла ошибка при создании ставки, повторите попытку позже';

export default class extends Controller {
    static targets = ['content', 'loader', 'companyValue']
    static values = {
        lotNumber: Number,
        price: Number,
    }

    declare readonly contentTarget: HTMLElement
    declare readonly hasContentTarget: boolean
    declare readonly loaderTarget: HTMLElement
    declare readonly hasLoaderTarget: boolean
    declare readonly companyValueTarget: HTMLInputElement
    declare readonly hasCompanyValueTarget: boolean

    declare lotNumberValue: number
    declare priceValue: number

    private loading = false
    private submitting = false

    async open(lotNumber: number, price: number) {
        if (this.loading) {
            return
        }

        this.lotNumberValue = lotNumber
        this.priceValue = price
        this.loading = true

        if (this.hasLoaderTarget) {
            this.loaderTarget.classList.remove('hidden')
        }

        if (this.hasContentTarget) {
            this.contentTarget.innerHTML = ''
        }

        this.element.dispatchEvent(new CustomEvent('modal:open'))

        try {
            const response = await fetch(`/lots/confirm-bid/${lotNumber}?price=${encodeURIComponent(String(price))}`, {
                headers: {
                    'X-Requested-With': 'XMLHttpRequest',
                },
            })

            const { html, error } = await response.json()

            if (!response.ok || !html) {
                this.showAlert(error || 'Ошибка при загрузке данных для создания ставки', 'error', 5000)
                return this.element.dispatchEvent(new CustomEvent('modal:hide'))
            }

            if (this.hasLoaderTarget) {
                this.loaderTarget.classList.add('hidden')
            }

            if (this.hasContentTarget) {
                this.contentTarget.innerHTML = html

                const defaultItem = this.contentTarget.querySelector<HTMLElement>('.with-dropdown__menu--item.selected')

                if (defaultItem) {
                    const selectInput = this.contentTarget.querySelector<HTMLInputElement>('[data-select-target="input"]')
                    const selectValue = this.contentTarget.querySelector<HTMLInputElement>('[data-select-target="value"]')

                    if (selectInput && selectValue) {
                        selectInput.value = defaultItem.textContent?.trim() ?? ''
                        selectValue.value = defaultItem.dataset.value ?? ''
                    }
                }
            }
        } finally {
            this.loading = false
        }
    }

    async confirm(event: Event) {
        event.preventDefault()
        event.stopPropagation()

        if (this.submitting || this.loading) {
            return
        }

        const lotNumber = this.lotNumberValue
        const price = this.priceValue

        if (!lotNumber || !price) {
            return
        }

        this.submitting = true

        try {
            const companyId = this.hasCompanyValueTarget && this.companyValueTarget.value
                ? Number(this.companyValueTarget.value)
                : null

            const response = await fetch('/api/bids/add', {
                method: 'POST',
                headers: {
                    'X-Requested-With': 'XMLHttpRequest',
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ price, lotNumber, companyId }),
            })

            const { error, success, errorCode, price: currentPrice, leaderUserHash } = await response.json() as BidAddResponse

            if (!response.ok || !success) {
                this.element.dispatchEvent(new CustomEvent('modal:hide'))
                this.showAlert(error || DEFAULT_ERROR_MESSAGE, 'error', 10000)

                if (errorCode === 'bid_not_greater_than_previous' && currentPrice != null) {
                    const outbidDetail: BidOutbidDetail = { lotNumber, price: currentPrice, leaderUserHash }
                    window.dispatchEvent(new CustomEvent('bid:outbid', { detail: outbidDetail }))
                }

                return
            }

            const detail: BidPlacedDetail = {
                lotNumber,
                price,
            }

            window.dispatchEvent(new CustomEvent('bid:placed', { detail }))
            this.element.dispatchEvent(new CustomEvent('modal:hide'))
            this.showAlert('Ваша ставка принята', 'success', 5000)
        } catch (response) {
            this.element.dispatchEvent(new CustomEvent('modal:hide'))
            this.showAlert(DEFAULT_ERROR_MESSAGE, 'error', 5000)
        } finally {
            this.submitting = false
        }
    }

    private showAlert(message: string, type: AlertType, timeout?: number) {
        window.dispatchEvent(new CustomEvent('alert:show', { detail: { message, type, timeout }, bubbles: true }))
    }
}
